/**
 * Google Analytics 4 — minimal pageview-only integration.
 *
 * - `installGtag()` injects the gtag.js script tag and config call once at
 *   app boot. No-op when VITE_GA_MEASUREMENT_ID is unset.
 * - `usePageviewTracking()` (a hook) fires a `page_view` event whenever
 *   the route changes. Mount it once in the App layout.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { env } from './env.ts';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let installed = false;

export function installGtag(): void {
  if (installed) return;
  if (!env.gaMeasurementId || typeof window === 'undefined') return;

  const id = env.gaMeasurementId;

  // Standard GA4 snippet — but we manage page_view manually via the
  // `send_page_view: false` flag so SPAs don't double-count.
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: false });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  installed = true;
}

export function usePageviewTracking(): void {
  const location = useLocation();
  useEffect(() => {
    if (!env.gaMeasurementId || typeof window === 'undefined') return;
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);
}
